/**
 * Agency brand switcher (header). Lists the caller's workspaces
 * (GET /api/v1/users/me/workspaces) and switches with
 * POST /api/v1/tenants/me/workspaces/{id}/switch, after which every API call
 * carries `X-Workspace-Id` (see lib/api/client.ts) and the app reloads so all
 * data is re-fetched in the new scope.
 *
 * HIDDEN unless the tenant has at least one workspace — workspaces are
 * flag-OFF today, so this renders nothing (and costs one cheap GET on mount).
 */

import { useEffect, useId, useRef, useState } from 'react';

import {
  clearWorkspace,
  getActiveWorkspaceId,
  listMyWorkspacesOrNull,
  switchWorkspace,
  type Workspace,
} from '@/lib/api/workspaces';
import { WORKSPACE_INVALID_EVENT } from '@/lib/api/client';
import { useToast } from '@/ui';
import { useT, tr } from '@/i18n';

export function WorkspaceSwitcher() {
  const { t } = useT();
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const panelId = useId();
  const toast = useToast();

  useEffect(() => {
    let alive = true;
    void listMyWorkspacesOrNull().then((w) => {
      if (!alive || w === null) return; // unreachable: keep current state untouched
      setWorkspaces(w);
      // Reconcile the persisted id with server truth: a deleted / no-longer-
      // granted workspace must not keep scoping every request to nothing.
      const stored = getActiveWorkspaceId();
      if (stored && !w.some((x) => x.id === stored)) {
        clearWorkspace();
        window.location.reload();
      }
    });
    const onInvalid = () => {
      clearWorkspace();
      window.location.reload();
    };
    window.addEventListener(WORKSPACE_INVALID_EVENT, onInvalid);
    return () => {
      alive = false;
      window.removeEventListener(WORKSPACE_INVALID_EVENT, onInvalid);
    };
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (workspaces.length === 0) return null;

  const activeId = getActiveWorkspaceId();
  // `null` = "Tout le compte" (no X-Workspace-Id → whole tenant).
  const active: Workspace | null = workspaces.find((w) => w.id === activeId) ?? null;

  const chooseAll = () => {
    if (busy) return;
    if (!activeId) {
      setOpen(false);
      return;
    }
    setBusy(true);
    clearWorkspace();
    window.location.reload();
  };

  const choose = async (w: Workspace) => {
    if (busy) return;
    if (active && w.id === active.id) {
      setOpen(false);
      return;
    }
    setBusy(true);
    try {
      await switchWorkspace(w.id);
      // Re-fetch everything in the new workspace scope.
      window.location.reload();
    } catch {
      setBusy(false);
      toast.error(t('shell.app.layoutsWorkspaceSwitcher.couldNotSwitchWorkspace'));
    }
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls={panelId}
        aria-label={t('shell.app.layoutsWorkspaceSwitcher.workspaceV', { v: active ? active.name : tr('shell.app.layoutsWorkspaceSwitcher.wholeAccount') })}
        data-testid="workspace-switcher"
        className="flex min-h-[44px] max-w-[11rem] items-center gap-1.5 rounded-token border border-border bg-surface-2/40 px-2 py-1.5 text-sm text-foreground transition-colors hover:bg-surface-2 focus:outline-none focus:ring-2 focus:ring-ring"
      >
        <svg aria-hidden="true" viewBox="0 0 24 24" className="h-4 w-4 shrink-0 text-muted" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M4 20V8l8-4 8 4v12M9 20v-6h6v6" />
        </svg>
        <span className="hidden truncate sm:inline">{active ? active.name : t('shell.app.layoutsWorkspaceSwitcher.wholeAccount')}</span>
        <svg aria-hidden="true" viewBox="0 0 24 24" className="h-3.5 w-3.5 shrink-0 text-muted" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <path d="m6 9 6 6 6-6" />
        </svg>
      </button>
      {open && (
        <div
          id={panelId}
          className="absolute left-0 top-full z-50 mt-1 w-64 overflow-hidden rounded-token-lg border border-border bg-surface py-1 shadow-elevation-3"
        >
          <div className="px-3 py-2 text-[10px] font-semibold uppercase tracking-wide text-muted">{t('shell.app.layoutsWorkspaceSwitcher.workspaces')}</div>
          <ul>
            <li>
              <button
                type="button"
                disabled={busy}
                onClick={chooseAll}
                data-testid="workspace-all"
                aria-current={!active ? 'true' : undefined}
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-foreground hover:bg-surface-2 focus:outline-none focus-visible:bg-surface-2 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring disabled:opacity-60"
              >
                <span className="min-w-0 flex-1 truncate">{t('shell.app.layoutsWorkspaceSwitcher.wholeAccount')}</span>
                {!active && <span aria-hidden="true" className="text-primary">✓</span>}
              </button>
            </li>
            {workspaces.map((w) => (
              <li key={w.id}>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => void choose(w)}
                  aria-current={active && w.id === active.id ? 'true' : undefined}
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-foreground hover:bg-surface-2 focus:outline-none focus-visible:bg-surface-2 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring disabled:opacity-60"
                >
                  <span className="min-w-0 flex-1 truncate">{w.name}</span>
                  {active && w.id === active.id && (
                    <span aria-hidden="true" className="text-primary">✓</span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
